import { useMutation, useQueryClient } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortBuilderDefinitionLegacy, CohortVersion } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { invalidateCohort } from './invalidateCohort';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';
import { cohortQueryKeys } from './queryKeys';

export interface ProcessCohortInput {
  projectId: string;
  profileId: string;
  versionId?: string;
  datasetReleaseId: string;
  dslVersion?: string;
  sqlValue?: string;
  jsonValue?: CohortBuilderDefinitionLegacy;
}

/**
 * Canonical TanStack mutation for triggering cohort execution
 * (POST /cohort-version/<id>/cohort-execution). Replaces the RTK
 * `usePostCohortExecutionMutation`.
 *
 * Optimistic updates: callers should set `isExecutionPending` + the
 * PROCESSING status on the Zustand store from their submit handler, then
 * roll back from `onError`. The cohort profile cache invalidates on
 * success so the server-confirmed status arrives.
 */
export const useProcessCohortMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ObjectResponse<CohortVersion>, Error, ProcessCohortInput>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: async ({
      projectId,
      profileId,
      versionId,
      datasetReleaseId,
      dslVersion,
      sqlValue,
      jsonValue,
    }) => {
      const url = `/${COHORTS_PATH}/${projectId}/cohort-profile/${profileId}/cohort-version/${versionId}/cohort-execution`;
      return authFetch<ObjectResponse<CohortVersion>>(url, {
        method: 'POST',
        body: JSON.stringify({ datasetReleaseId, dslVersion, sqlValue, jsonValue }),
      });
    },
    onSuccess: (_data, { projectId, profileId, versionId }) => {
      // Invalidate the cohort LIST + profile reads. Status keeps polling
      // until the backend transitions out of PROCESSING.
      void invalidateCohort(queryClient, { cohortId: profileId, projectId, versionId });
      // Processing a cohort (the real Save, not Save Draft) can make any
      // analytical dataset built on top of it stale — we don't know which
      // ones reference this cohort, so invalidate the whole kind broadly.
      void queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.assetStalenessKind('analytical_dataset'),
      });
    },
  });
};
